/**
 * Sidebar — FU-E global left rail.
 *
 * Vertical 220px-wide (collapsible to 64px) Linear-style nav rail.
 *
 * Phase 3.6.B layout shuffle:
 *   - The bottom Notifications bell + Profile menu moved BACK up to
 *     `<AppShell>`'s top-right cluster (alongside the running-timer pill).
 *     The rail no longer owns user-context affordances.
 *   - The Settings gear stayed on the rail but its link target swapped
 *     from project-scoped (`/projects/{id}/settings`) to tenant-scoped
 *     (`/settings`). Project-scoped settings now hang off the board's
 *     own header gear icon.
 *   - The freed bottom space gets a small "© 3mote" footer for visual
 *     anchor — Linear's rail does the same with a build hash.
 *
 * Layout:
 *   [collapse toggle (top, on collapsed) / panel-close icon (top, on expanded)]
 *   [3mote mark (+ wordmark when expanded)]
 *   [+ Create new]
 *   ──────────────────────
 *   Boards
 *   Wiki
 *   Chat
 *   Timesheets    ┐ hidden for guest accounts — see `isGuest` in <Sidebar>
 *   Reports       ┘
 *   ⋯ (mt-auto spacer)
 *   Settings (tenant-scoped → /settings)
 *   ──────────────────────
 *   © 3mote (footer text, hidden when collapsed)
 *
 * State persistence:
 *   - `localStorage['sidebar-collapsed']` — `'1'` collapsed, `'0'` /
 *     missing expanded.
 */

import { Link, useLocation, useMatches } from '@tanstack/react-router'
import {
  BarChart3,
  BookOpen,
  Clock,
  type Columns3,
  MessageSquare,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Settings,
} from 'lucide-react'
import { type ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { LogoMark } from '@/components/Logo'
import { useAgentWorkingRealtime } from '@/features/agents/use-agent-working-realtime'
import { useConversations } from '@/features/chat/hooks/use-conversations'
import { useDockBadge } from '@/features/chat/hooks/use-dock-badge'
import { unreadLabel } from '@/features/chat/lib/unread-label'
import { useChatGlobalRealtime } from '@/features/chat/realtime/use-chat-global-realtime'
import { CreateNewModal } from '@/features/create-new/CreateNewModal'
import { useIsGuest } from '@/features/me/guest'
import { useMe } from '@/features/me/use-me'
import { useReportsPermission } from '@/features/me/use-reports-permission'
import { useGlobalRealtime } from '@/features/notifications/use-global-realtime'
import { usePresence } from '@/features/presence/use-presence'
import { useProjects } from '@/features/projects/use-projects'
import { useTimesheetsMenu } from '@/features/time/use-timesheets-menu'
import type { WebSocketStatus } from '@/lib/use-websocket'

import { SidebarFavoritesSection } from '@/features/wiki/components/SidebarFavoritesSection'

const COLLAPSED_KEY = 'sidebar-collapsed'

/**
 * Read the collapsed state lazily on mount. SSR safety isn't a concern
 * (Vite SPA), but `localStorage` access during SSR-ish builds would
 * still trip — guard for safety.
 */
function readCollapsedInitial(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

/* -------------------------------------------------------------------------- */
/*                              nav row primitive                             */
/* -------------------------------------------------------------------------- */

interface NavItemProps {
  /**
   * Lucide icon component (renders at 18×18 / strokeWidth 1.75). For
   * non-Lucide icons (e.g. the 3mote brand mark on the Boards row),
   * pass `iconNode` instead. Optional only when `iconNode` is provided.
   */
  icon?: typeof Columns3
  /**
   * Pre-rendered icon override — used when the row needs a custom glyph
   * that doesn't fit the Lucide stroke-icon model (e.g. the colored
   * 3mote `<LogoMark>` on the Boards row, per David's whitelabel
   * direction). When supplied, `icon` is ignored.
   */
  iconNode?: ReactNode
  label: string
  active?: boolean
  collapsed: boolean
  onClick?: () => void
  href?: string
  disabled?: boolean
  /**
   * Optional unread badge text rendered to the right of the label
   * (expanded) or as a tiny dot overlay on the icon (collapsed). Phase
   * 4a: the Messages row shows the summed `unreadCount` across DMs.
   * Empty / falsy → no badge.
   */
  badgeText?: string | null
}

function NavItem({
  icon: Icon,
  iconNode,
  label,
  active,
  collapsed,
  onClick,
  href,
  disabled,
  badgeText,
}: NavItemProps) {
  const { t } = useTranslation('common')
  // Colour classes on this row (`text-fg-muted` / `text-fg`) are TEXT
  // classes: they reach a Lucide icon through `currentColor` on purpose,
  // and they must NOT recolour the 3mote `<LogoMark>` on the Boards row.
  // That mark paints its frame with a fixed `#000000` and its bars with
  // the brand triad — no `currentColor` anywhere in it — so the row's
  // text colour cannot leak into the SVG. (It used to: the frame was
  // `currentColor`, so the row's muted/dark-theme text colour painted a
  // grey-then-white frame.) Disabled: greyed out, hover stays muted.
  //
  // Layout differs by state:
  //   - Collapsed: 36×36 square button, centered horizontally in the
  //     44px-wide rail column. The hover/active fill reads as a clean
  //     square indicator (no off-center icon, no rectangle bleed).
  //   - Expanded: full-width row with the icon at the left and a gap
  //     before the label.
  const layout = collapsed
    ? 'h-9 w-9 mx-auto justify-center'
    : 'h-9 w-full justify-start gap-3 px-2.5'
  // Active row uses a soft slate fill instead of slate-900 — same neutral
  // pattern Linear / Slack / Notion / GitHub use for chrome row selection.
  // The lighter background also means colorful icons (the 3mote LogoMark
  // on the Boards row, future kind icons, etc.) stay vibrant without
  // needing a tone-flip when active.
  //   - Active:  bg-bg-muted text-fg font-semibold
  //   - Hover:   bg-bg-muted  text-fg (default state on hover)
  //   - Default: text-fg-muted
  // `font-semibold` differentiates active from hover without needing a
  // left-edge accent — the bolder text reads as "selected" against the
  // unbolded hover state.
  const base = `group relative flex items-center rounded-md text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-accent ${layout}`
  const tone = disabled
    ? 'font-medium text-fg-subtle cursor-not-allowed'
    : active
      ? 'bg-bg-muted text-fg font-semibold'
      : 'font-medium text-fg-muted hover:bg-bg-muted hover:text-fg'

  const className = `${base} ${tone}`.trim()
  const showBadge = Boolean(badgeText)
  const iconElement =
    iconNode ??
    (Icon ? <Icon size={18} strokeWidth={1.75} className="shrink-0" aria-hidden="true" /> : null)
  const inner = (
    <>
      <span className="relative flex shrink-0 items-center">
        {iconElement}
        {showBadge && collapsed && (
          <span
            aria-hidden="true"
            className="absolute -end-1 -top-1 inline-flex h-2 w-2 rounded-full bg-rose-500 ring-2 ring-bg-elevated"
          />
        )}
      </span>
      {!collapsed && <span className="truncate">{label}</span>}
      {!collapsed && showBadge && (
        <span
          aria-label={t('sidebar.unreadAriaLabel', { badge: badgeText ?? '' })}
          className="ms-auto inline-flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full bg-slate-700 px-1.5 text-[11px] font-semibold text-white"
        >
          {badgeText}
        </span>
      )}
      {collapsed && (
        <span
          className="pointer-events-none absolute left-full top-1/2 z-50 ms-2 -translate-y-1/2 whitespace-nowrap rounded-md bg-fg px-2 py-1 text-xs font-medium text-bg opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
          role="tooltip"
        >
          {label}
        </span>
      )}
    </>
  )

  if (href && !disabled) {
    return (
      <Link
        to={href}
        className={className}
        aria-current={active ? 'page' : undefined}
        title={collapsed ? label : undefined}
        // Fire the side-effect callback (e.g. re-open a contextual
        // submenu) BEFORE the router navigation. TanStack's `<Link>`
        // honors `onClick` and still follows `to=`.
        onClick={onClick}
      >
        {inner}
      </Link>
    )
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={className}
      title={collapsed ? label : undefined}
      aria-label={label}
    >
      {inner}
    </button>
  )
}

/* -------------------------------------------------------------------------- */
/*                          Realtime status indicator                          */
/* -------------------------------------------------------------------------- */

const WS_STATUS_DOT_CLASS: Record<WebSocketStatus, string> = {
  open: 'bg-emerald-500',
  connecting: 'bg-amber-400 animate-pulse',
  reconnecting: 'bg-amber-400 animate-pulse',
  closed: 'bg-fg-subtle',
  // Idle = the WS was deliberately closed after the user went inactive
  // (ADR-033). Slate-300 dot, no pulse — distinct from 'closed' (which
  // means "we couldn't connect") and from 'reconnecting' (amber, busy).
  idle: 'bg-slate-300',
}

/**
 * RealtimeStatusPill — sidebar-bottom indicator for the global
 * WebSocket subscription status. Always renders (per David's call —
 * "I'm fine with it always showing"). Collapsed rail shows just the
 * colored dot centered in the 36×36 cell with the label as a tooltip;
 * expanded shows the dot + text.
 */
function RealtimeStatusPill({
  status,
  collapsed,
}: {
  status: WebSocketStatus
  collapsed: boolean
}) {
  const { t } = useTranslation('common')
  // The status key resolution is intentional: each status maps to its own
  // catalog key so translators can use idiomatic labels, not a single
  // interpolated string. Inline switch keeps the keys statically known to
  // the typed `t()` so `ParseKeys` can validate them.
  const label =
    status === 'open'
      ? t('sidebar.realtimeLive')
      : status === 'connecting'
        ? t('sidebar.realtimeConnecting')
        : status === 'reconnecting'
          ? t('sidebar.realtimeReconnecting')
          : status === 'idle'
            ? t('sidebar.realtimeIdle')
            : t('sidebar.realtimeOffline')
  const dot = WS_STATUS_DOT_CLASS[status]
  const titleText = t('sidebar.realtimeStatusTitle', { label })
  if (collapsed) {
    return (
      <span
        className="group relative inline-flex h-7 w-7 items-center justify-center self-center rounded-md"
        title={titleText}
        aria-live="polite"
      >
        <span className={`h-2 w-2 rounded-full ${dot}`} aria-hidden="true" />
        <span className="sr-only">{t('sidebar.realtimeStatusSrLabel', { label })}</span>
        <span
          role="tooltip"
          className="pointer-events-none absolute left-full top-1/2 z-50 ms-2 -translate-y-1/2 whitespace-nowrap rounded-md bg-fg px-2 py-1 text-xs font-medium text-bg opacity-0 shadow-lg transition-opacity group-hover:opacity-100"
        >
          {titleText}
        </span>
      </span>
    )
  }
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] text-fg-muted"
      aria-live="polite"
      title={titleText}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} aria-hidden="true" />
      <span className="sr-only">{t('sidebar.realtimeStatusSrPrefix')}</span>
      {label}
    </span>
  )
}

/* -------------------------------------------------------------------------- */
/*                                 Sidebar                                    */
/* -------------------------------------------------------------------------- */

export function Sidebar() {
  const { t } = useTranslation('common')
  const location = useLocation()
  const matches = useMatches()
  const projectsQuery = useProjects()
  const me = useMe()
  /*
   * A guest sees Boards / Wiki / Chat / Settings and nothing else.
   * Timesheets is the load-bearing removal: the BE 403s every
   * time-tracking route for a guest, so the row is a dead end rather than
   * a reduced surface. Reports goes with it — an org-wide performance
   * dashboard is not an external account's business. The Settings row
   * stays and its tabs already self-filter by permission, so a guest lands
   * on My Profile + Notifications with nothing to 403 on.
   */
  const isGuest = useIsGuest()
  // Reports is the first slug-gated READ surface, so the rail row is
  // hidden when the caller lacks `view-reports` rather than rendering a
  // link whose only outcome is the NoAccess panel. The hook falls OPEN
  // while `/api/me` is in flight, so the row never flickers away from
  // somebody who does hold the slug.
  const { canViewReports } = useReportsPermission()
  // Global realtime subscription — drives notification bell, running
  // timer state, cross-tab card propagation. Pill rendered at the
  // bottom of the rail.
  const { wsStatus } = useGlobalRealtime()
  // `agent.working` frames feed the card glow store (board, master, card modal).
  useAgentWorkingRealtime()
  // Presence bootstrap fires here so the cache is seeded with
  // the full tenant snapshot BEFORE any incoming WS event mutates it.
  // Wiring at AppShell scope (vs. only in the chat sidebar) is load-
  // bearing: when a fresh window opens on a non-chat route, the WS self-
  // broadcast lands in the cache first, and `staleTime: Infinity` on
  // the chat-only mount would have skipped the bootstrap (the cache
  // looks "fresh" even though it only knows about self). Return value
  // intentionally unused — we just need the query to fire.
  usePresence()
  // Phase 4a — chat global realtime subscription. Mounts adjacent to
  // the existing global realtime listener and invalidates the
  // `['conversations']` cache on every chat frame. Status is currently
  // ignored on the visible rail (the existing pill represents the
  // shared WebSocket lifecycle).
  useChatGlobalRealtime()
  const conversations = useConversations()
  // macOS / Windows dock-icon badge mirroring the
  // total unread count across every conversation. No-op in regular
  // browser tabs; works when 3mote is installed as a PWA (Chrome
  // Install) or added to dock via Safari.
  useDockBadge(conversations.data)

  // Whitelabel gate (per David's tier rule): only paid plans can replace
  // the 3MOTE brand row with their own logo. Free / starter plan keeps
  // the default 3mote branding regardless of whether they uploaded a
  // logo. The Boards row's 3mote-icon-as-glyph stays universal — that's
  // legacy parity, not a tier feature.
  //
  // Read `effectivePlan`, NEVER the raw `plan` column: a comped workspace
  // (ours, design partners) carries a `plan_override` that leaves `plan`
  // sitting at `starter` while every capacity ceiling already resolves as
  // premium. Gating on `plan` here made this the one plan-derived
  // decision in the app that disagreed with all the others, and it is why
  // our own tenant rendered the 3MOTE wordmark on prod.
  //
  // The favicon is intentionally NOT swapped — product-level branding,
  // not workspace-level. (Per David: "the tenant's site favicon should
  // remain our 3mote logo.")
  const tenantLogoUrl = me.data?.tenant.logoUrl ?? null
  const tenantPlan = me.data?.tenant.effectivePlan
  const tenantName = me.data?.tenant.name ?? ''
  const whitelabelEligible = tenantPlan !== undefined && tenantPlan !== 'starter'
  const showWhitelabel = whitelabelEligible && Boolean(tenantLogoUrl)

  // Derive the deepest projectId from route params so the create-new
  // modal can default to the active board when the user clicks +.
  const projectIdFromRoute = (() => {
    for (let i = matches.length - 1; i >= 0; i -= 1) {
      const m = matches[i]
      if (!m) continue
      const params = m.params as Record<string, string | undefined> | undefined
      if (params?.projectId) return params.projectId
    }
    return null
  })()

  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsedInitial())
  const [createNewOpen, setCreateNewOpen] = useState(false)

  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0')
    } catch {
      // Quota exceeded / private mode — tolerate.
    }
  }, [collapsed])

  const projects = projectsQuery.data ?? []
  const path = location.pathname
  const boardsActive = path === '/' || path.startsWith('/board/')
  const docsActive = path === '/wiki' || path.startsWith('/wiki/')
  const timesheetsActive = path.startsWith('/timesheet')
  const reportsActive = path.startsWith('/reports')
  const messagesActive = path === '/messages' || path.startsWith('/messages/')
  const settingsActive = path === '/settings' || path.startsWith('/settings/')

  // Sum of unread counts (top-level + thread replies) → "999+" cap →
  // null when zero. Mirrors the chat-sidebar per-row badge so the two
  // surfaces always agree on the total.
  const unreadTotal = (conversations.data ?? []).reduce(
    (acc, c) => acc + (c.unreadCount ?? 0) + (c.unreadThreadReplyCount ?? 0),
    0,
  )
  const messagesBadge = unreadTotal > 0 ? unreadLabel(unreadTotal) : null

  // Timesheets sub-menu shared state — clicking the "Timesheets" nav item
  // from anywhere (even when already on /timesheet) re-opens the submenu,
  // matching the legacy `<SubItems>` pattern.
  const timesheetsMenu = useTimesheetsMenu()

  const widthClass = collapsed ? 'w-16' : 'w-[220px]'

  return (
    <>
      <aside
        aria-label={t('sidebar.primaryAriaLabel')}
        className={`flex h-screen shrink-0 flex-col border-e border-border bg-bg-elevated transition-[width] duration-150 ease-out ${widthClass}`}
      >
        {/* Brand row + collapse toggle. */}
        <div className="flex flex-col gap-1.5 px-2.5 pb-2 pt-3">
          <div
            className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'} gap-1`}
          >
            <Link
              to="/"
              aria-label={
                showWhitelabel
                  ? t('sidebar.homeAriaLabelTenant', { tenantName })
                  : t('sidebar.homeAriaLabelDefault')
              }
              className="flex h-9 items-center rounded-md px-0.5 text-fg hover:bg-bg-muted"
            >
              {showWhitelabel && tenantLogoUrl ? (
                <>
                  {/* Tenant logo at 28px — same footprint as <LogoMark>
                      so the brand row's vertical rhythm doesn't shift
                      when whitelabel toggles. `object-contain` preserves
                      arbitrary aspect ratios (square / wide / tall logos
                      all render without distortion). */}
                  <img
                    src={tenantLogoUrl}
                    alt=""
                    aria-hidden="true"
                    className="h-7 w-7 shrink-0 rounded-md object-contain"
                  />
                  {!collapsed && (
                    <span className="ms-2 truncate text-[16px] font-semibold leading-none text-fg">
                      {tenantName}
                    </span>
                  )}
                </>
              ) : (
                <>
                  <LogoMark size={28} tone="dark" />
                  {!collapsed && (
                    <span
                      className="ms-1 text-[24px] font-bold leading-none tracking-[-0.02em] text-fg"
                      style={{
                        fontFamily:
                          "'Rubik', system-ui, -apple-system, 'Helvetica Neue', Arial, sans-serif",
                      }}
                    >
                      3MOTE
                    </span>
                  )}
                </>
              )}
            </Link>
            {!collapsed && (
              <button
                type="button"
                onClick={() => setCollapsed(true)}
                aria-label={t('sidebar.collapseAriaLabel')}
                title={t('sidebar.collapseAriaLabel')}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-subtle transition-colors hover:bg-bg-muted hover:text-fg focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <PanelLeftClose size={18} aria-hidden />
              </button>
            )}
          </div>
          {collapsed && (
            <button
              type="button"
              onClick={() => setCollapsed(false)}
              aria-label={t('sidebar.expandAriaLabel')}
              title={t('sidebar.expandAriaLabel')}
              className="group relative inline-flex h-8 w-full items-center justify-center rounded-md text-fg-subtle transition-colors hover:bg-bg-muted hover:text-fg focus:outline-none focus:ring-2 focus:ring-accent"
            >
              <PanelLeftOpen size={18} aria-hidden />
              <span
                className="pointer-events-none absolute left-full top-1/2 z-50 ms-2 -translate-y-1/2 whitespace-nowrap rounded-md bg-fg px-2 py-1 text-xs font-medium text-bg opacity-0 shadow-lg transition-opacity group-hover:opacity-100"
                role="tooltip"
              >
                {t('sidebar.expandAriaLabel')}
              </span>
            </button>
          )}

          {/*
            Hidden for guests. The dialog holds exactly two items — Time
            entry and Project — and the BE refuses BOTH for a guest with
            403 `guest_forbidden`, so 100% of this control's contents are
            dead ends and a guest has no create affordance that would
            work. Same rule the chat rail follows: don't render a control
            whose only outcome is a refusal toast.
          */}
          {!isGuest && (
            <button
              type="button"
              onClick={() => setCreateNewOpen(true)}
              aria-label={t('sidebar.createNewAriaLabel')}
              title={collapsed ? t('sidebar.createNewLabel') : undefined}
              // Soft white CTA — David's call after trying navy + royal blue:
              // both filled treatments overpowered the rail. The bordered-
              // white variant is restrained, matches the rail's overall
              // weight, and lets the brand mark above carry the visual
              // interest. The Plus glyph + bold label still read as a
              // clear "primary affordance" against the white background.
              //
              // Collapsed state matches NavItem: 36×36 square, centered
              // in the rail column, no padding/gap.
              className={`group relative mt-1 flex items-center rounded-md border border-border bg-bg-elevated text-sm font-medium text-fg shadow-sm transition-colors hover:border-border-strong hover:bg-bg-muted focus:outline-none focus:ring-2 focus:ring-accent ${
                collapsed ? 'h-9 w-9 mx-auto justify-center' : 'h-9 w-full justify-start gap-2 px-2'
              }`}
            >
              <Plus size={16} strokeWidth={2} className="shrink-0 text-fg" aria-hidden />
              {!collapsed && <span>{t('sidebar.createNewLabel')}</span>}
              {collapsed && (
                <span
                  role="tooltip"
                  className="pointer-events-none absolute left-full top-1/2 z-50 ms-2 -translate-y-1/2 whitespace-nowrap rounded-md bg-fg px-2 py-1 text-xs font-medium text-bg opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                >
                  {t('sidebar.createNewLabel')}
                </span>
              )}
            </button>
          )}
        </div>

        {/* Primary nav */}
        <nav
          className="flex flex-col gap-0.5 px-2.5 py-2"
          aria-label={t('sidebar.sectionsAriaLabel')}
        >
          <NavItem
            iconNode={
              // Legacy parity: the Boards row carries the 3mote brand
              // mark as its glyph — keeps product branding visible in
              // every tab even when the top-of-rail brand row is
              // whitelabeled out for paid tenants. `tone="dark"` always:
              // the frame is a fixed black that matches the favicon and
              // the bars are the brand triad, so the mark renders
              // identically on every theme and on either the hover or
              // the active row fill. (The pre-2026-04-30 active row was
              // slate-900 and forced a `tone="light"` flip — the new
              // soft-fill active state retires that hack.)
              <LogoMark size={18} tone="dark" decorative className="shrink-0" />
            }
            label={t('sidebar.boardsLabel')}
            active={boardsActive}
            collapsed={collapsed}
            href="/"
          />
          <NavItem
            icon={BookOpen}
            label={t('sidebar.documentationLabel')}
            active={docsActive}
            collapsed={collapsed}
            href="/wiki"
          />
          <NavItem
            icon={MessageSquare}
            label={t('sidebar.messagesLabel')}
            active={messagesActive}
            collapsed={collapsed}
            href="/messages"
            badgeText={messagesBadge}
          />
          {!isGuest && (
            <>
              <NavItem
                // Same Clock glyph the per-card timer button uses, so the
                // "track time" affordance reads as one concept across surfaces.
                icon={Clock}
                label={t('sidebar.timesheetsLabel')}
                active={timesheetsActive}
                collapsed={collapsed}
                href="/timesheet"
                // Click also re-opens the Timesheets submenu — same shape
                // as the legacy parent-row toggle that revealed the SubItems
                // drawer. If the user already collapsed the submenu, this
                // brings it back without making them hunt for a separate
                // toggle.
                onClick={() => timesheetsMenu.setOpen(true)}
              />
              {canViewReports && (
                <NavItem
                  // Reports = the performance dashboard (story points and
                  // completed work items by person, project and period).
                  // Distinct from Timesheets, which is the raw time-entry
                  // ledger.
                  icon={BarChart3}
                  label={t('sidebar.reportsLabel')}
                  active={reportsActive}
                  collapsed={collapsed}
                  href="/reports"
                />
              )}
              {/* Passwords and VPN rows lived here. Neither feature is
                  built and neither is close — re-add the rows when the
                  surfaces ship. */}
            </>
          )}
        </nav>

        {/* Wiki Favorites — the user's pinned wiki nodes, lifted out of
            the doc rail (2026-05-28) into a second sidebar group below
            the 7 nav items. Rail-native row chrome matches `<NavItem>`;
            a `border-t border-border` + small caps label signals the
            group break. Pinning interaction (right-click in the wiki
            tree → "Add to Favorites") unchanged. */}
        <SidebarFavoritesSection collapsed={collapsed} />

        {/* Bottom cluster — tenant Settings + realtime status pill +
            minimal footer. The realtime pill replaces the BoardHeader's
            former in-header pill (FU — David: "move it into the left
            sidebar at the bottom below settings"). Lives here because
            Sidebar is always-mounted on authed routes and the WS state
            is a global concern, not a board-only one. */}
        <div className="mt-auto flex flex-col gap-2 border-t border-border px-2.5 py-2">
          <NavItem
            icon={Settings}
            label={t('sidebar.settingsLabel')}
            active={settingsActive}
            collapsed={collapsed}
            href="/settings"
          />
          <RealtimeStatusPill status={wsStatus} collapsed={collapsed} />
          {!collapsed && (
            <p
              className="select-none px-2 pb-1 pt-0.5 text-[10px] uppercase tracking-wider text-fg-subtle"
              aria-hidden="true"
            >
              {t('sidebar.footerCopy')}
            </p>
          )}
        </div>
      </aside>

      <CreateNewModal
        open={createNewOpen}
        onClose={() => setCreateNewOpen(false)}
        currentProjectId={projectIdFromRoute}
        projects={projects}
      />
    </>
  )
}
